import * as THREE from 'three';

export default class Cactus {
    constructor(scene, physics, position = { x: 0, z: 0 }, sizeFactor = 1, style = 'saguaro') {
        this.scene = scene;
        this.physics = physics;
        this.position = position;
        this.sizeFactor = sizeFactor; // Controls overall cactus size
        this.style = style; // 'saguaro', 'barrel', or 'prickly'
        
        // Cactus properties
        this.height = 0;
        this.mesh = null;
        this.cactusGroup = new THREE.Group();
        this.collider = null;
        
        // Create the cactus
        this.create();
    }
    
    create() {
        // Choose cactus style if random
        if (this.style === 'random') {
            const styles = ['saguaro', 'barrel', 'prickly'];
            this.style = styles[Math.floor(Math.random() * styles.length)];
        }
        
        // Create the appropriate cactus type
        switch(this.style) {
            case 'saguaro':
                this.createSaguaroCactus();
                break;
            case 'barrel':
                this.createBarrelCactus();
                break;
            case 'prickly':
                this.createPricklyCactus();
                break;
            default:
                this.createSaguaroCactus();
        }
        
        // Add the cactus group to the scene
        this.scene.add(this.cactusGroup);
        
        // Add physics collider
        this.createPhysicsCollider();
    }
    
    createSaguaroCactus() {
        // Base height with variation
        this.height = (3 + Math.random() * 2) * this.sizeFactor;
        
        // Create cactus material with slight variation in green color
        const hue = 0.3 + (Math.random() * 0.1); // Green hue
        const saturation = 0.4 + (Math.random() * 0.2);
        const lightness = 0.25 + (Math.random() * 0.15);
        const color = new THREE.Color().setHSL(hue, saturation, lightness);
        
        const cactusMaterial = new THREE.MeshStandardMaterial({
            color: color,
            roughness: 0.8,
            metalness: 0.1,
            flatShading: true
        });
        
        // Create main trunk
        const trunkGeometry = new THREE.CylinderGeometry(
            0.4 * this.sizeFactor, 
            0.5 * this.sizeFactor, 
            this.height, 
            8
        );
        const trunk = new THREE.Mesh(trunkGeometry, cactusMaterial);
        trunk.position.y = this.height / 2;
        trunk.castShadow = true;
        trunk.receiveShadow = true;
        this.cactusGroup.add(trunk);
        
        // Add arms with probability
        if (Math.random() > 0.3) {
            // Left arm
            const leftArmHeight = this.height * (0.4 + Math.random() * 0.3);
            const leftArmGeometry = new THREE.CylinderGeometry(
                0.25 * this.sizeFactor,
                0.3 * this.sizeFactor,
                leftArmHeight,
                8
            );
            const leftArm = new THREE.Mesh(leftArmGeometry, cactusMaterial);
            
            // Position and rotate left arm
            const leftArmY = this.height * (0.5 + Math.random() * 0.2);
            leftArm.position.set(0, leftArmY, 0);
            leftArm.rotation.z = Math.PI / 4 + (Math.random() * 0.2); // ~45 degrees up
            leftArm.position.x = 0.5 * this.sizeFactor;
            leftArm.position.y = 0;
            leftArm.castShadow = true;
            
            // Create a group for the arm to handle rotation properly
            const leftArmGroup = new THREE.Group();
            leftArmGroup.position.y = leftArmY;
            leftArmGroup.add(leftArm);
            leftArm.position.y = leftArmHeight / 2;
            
            this.cactusGroup.add(leftArmGroup);
            
            // Maybe add right arm too
            if (Math.random() > 0.4) {
                const rightArmHeight = this.height * (0.3 + Math.random() * 0.3);
                const rightArmGeometry = new THREE.CylinderGeometry(
                    0.25 * this.sizeFactor,
                    0.3 * this.sizeFactor,
                    rightArmHeight,
                    8
                );
                const rightArm = new THREE.Mesh(rightArmGeometry, cactusMaterial);
                
                // Position and rotate right arm
                const rightArmY = this.height * (0.4 + Math.random() * 0.2);
                rightArm.position.set(0, rightArmY, 0);
                rightArm.rotation.z = -Math.PI / 4 - (Math.random() * 0.2); // ~-45 degrees up
                rightArm.position.x = -0.5 * this.sizeFactor;
                rightArm.position.y = 0;
                rightArm.castShadow = true;
                
                // Create a group for the arm to handle rotation properly
                const rightArmGroup = new THREE.Group();
                rightArmGroup.position.y = rightArmY;
                rightArmGroup.add(rightArm);
                rightArm.position.y = rightArmHeight / 2;
                
                this.cactusGroup.add(rightArmGroup);
            }
        }
        
        // Position the entire cactus at terrain height
        const saguaroHM = this.scene?.userData?.terrainHeightMap;
        const saguaroTY = saguaroHM ? saguaroHM.getHeight(this.position.x, this.position.z) : 0;
        this.cactusGroup.position.set(this.position.x, saguaroTY, this.position.z);
    }

    createBarrelCactus() {
        // Base size with variation
        const radius = (0.8 + Math.random() * 0.6) * this.sizeFactor;
        this.height = (1.2 + Math.random() * 0.8) * this.sizeFactor;
        
        // Create cactus material with slightly different green color
        const hue = 0.25 + (Math.random() * 0.1); // Yellowish-green hue
        const saturation = 0.5 + (Math.random() * 0.2);
        const lightness = 0.3 + (Math.random() * 0.1);
        const color = new THREE.Color().setHSL(hue, saturation, lightness);
        
        const cactusMaterial = new THREE.MeshStandardMaterial({
            color: color,
            roughness: 0.7,
            metalness: 0.1,
            flatShading: true
        });
        
        // Create barrel/capsule shape
        const barrelGeometry = new THREE.CapsuleGeometry(
            radius,
            this.height - (radius * 2),
            8,
            8
        );
        const barrel = new THREE.Mesh(barrelGeometry, cactusMaterial);
        barrel.position.y = this.height / 2;
        barrel.castShadow = true;
        barrel.receiveShadow = true;
        this.cactusGroup.add(barrel);
        
        // Position the cactus at terrain height
        const barrelHM = this.scene?.userData?.terrainHeightMap;
        const barrelTY = barrelHM ? barrelHM.getHeight(this.position.x, this.position.z) : 0;
        this.cactusGroup.position.set(this.position.x, barrelTY, this.position.z);
    }

    createPricklyCactus() {
        // Base size with variation
        this.height = (1.0 + Math.random() * 1.0) * this.sizeFactor;
        
        // Create cactus material with bluish-green color
        const hue = 0.33 + (Math.random() * 0.08); // Slightly bluish-green hue
        const saturation = 0.4 + (Math.random() * 0.3);
        const lightness = 0.3 + (Math.random() * 0.1);
        const color = new THREE.Color().setHSL(hue, saturation, lightness);
        
        const cactusMaterial = new THREE.MeshStandardMaterial({
            color: color,
            roughness: 0.8,
            metalness: 0.1,
            flatShading: true
        });
        
        // Create prickly pear pads
        const numPads = 3 + Math.floor(Math.random() * 4);
        
        for (let i = 0; i < numPads; i++) {
            // Create an oval pad
            const padGeometry = new THREE.SphereGeometry(
                0.8 * this.sizeFactor,
                8,
                8
            );
            
            // Flatten the sphere to make it pad-like
            padGeometry.scale(0.5, 0.8, 0.2);
            
            const pad = new THREE.Mesh(padGeometry, cactusMaterial);
            pad.castShadow = true;
            pad.receiveShadow = true;
            
            // Position pads relative to each other
            const angle = (i / numPads) * Math.PI * 2;
            const radius = 0.3 * this.sizeFactor;
            pad.position.set(
                Math.cos(angle) * radius,
                0.8 * this.height * (0.5 + (i / numPads) * 0.5),
                Math.sin(angle) * radius
            );
            
            // Rotate pad to face outward
            pad.rotation.y = angle + Math.PI / 2;
            pad.rotation.x = Math.random() * 0.3;
            
            this.cactusGroup.add(pad);
        }
        
        // Position the entire cactus at terrain height
        const pricklyHM = this.scene?.userData?.terrainHeightMap;
        const pricklyTY = pricklyHM ? pricklyHM.getHeight(this.position.x, this.position.z) : 0;
        this.cactusGroup.position.set(this.position.x, pricklyTY, this.position.z);
    }

    createPhysicsCollider() {
        if (!this.physics || !this.physics.isInitialized) return;
        
        // Create a cylinder collider that approximates the cactus shape
        let colliderRadius, colliderHeight;
        
        if (this.style === 'saguaro') {
            colliderRadius = 0.5 * this.sizeFactor;
            colliderHeight = this.height;
        } else if (this.style === 'barrel') {
            colliderRadius = 0.8 * this.sizeFactor;
            colliderHeight = this.height;
        } else { // prickly
            colliderRadius = 0.8 * this.sizeFactor;
            colliderHeight = this.height;
        }
        
        const colliderHM = this.scene?.userData?.terrainHeightMap;
        const colliderTY = colliderHM ? colliderHM.getHeight(this.position.x, this.position.z) : 0;
        const colliderPosition = new THREE.Vector3(
            this.position.x,
            colliderTY + colliderHeight / 2,
            this.position.z
        );
        
        // Create static cylinder collider
        this.collider = this.physics.createStaticCylinder(
            colliderPosition,
            colliderRadius,
            colliderHeight,
            0.7 // Higher friction for cacti
        );
        
        // Store reference to mesh for collision visualization if needed
        this.collider.visualMesh = this.cactusGroup;
        
        // Register cactus as an obstacle
        this.registerAsObstacle(colliderRadius);
    }
    
    registerAsObstacle(radius) {
        // Add cactus to obstacles list if game has one
        if (this.scene.userData && this.scene.userData.obstacles) {
            this.scene.userData.obstacles.push({
                position: { 
                    x: this.position.x, 
                    y: this.height / 2, 
                    z: this.position.z 
                },
                radius: radius * 1.5 // Slightly larger than visual size
            });
        }
    }
    
    // For removing the cactus from the scene if needed
    remove() {
        // Remove cactus group from scene
        if (this.cactusGroup) {
            this.scene.remove(this.cactusGroup);
        }
        
        // Remove from physics world if collider exists
        if (this.physics && this.physics.bodies && this.collider) {
            const index = this.physics.bodies.indexOf(this.collider);
            if (index !== -1) {
                this.physics.bodies.splice(index, 1);
            }
            
            const colliderIndex = this.physics.colliders.indexOf(this.collider);
            if (colliderIndex !== -1) {
                this.physics.colliders.splice(colliderIndex, 1);
            }
        }
    }
} 